<script setup lang="ts">
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Loading placeholder that mirrors the layout of {@link
 * file:src/components/AudioFileIssues/AudioFileIssueCard.vue}: a square cover
 * tile on the left with a metadata baseline, and title / artist / album /
 * badge / footer rows on the right. Rendered in place of the card list while
 * the first page of results is still loading so the card view never collapses
 * to a blank pane. Container-query classes match the real card so the skeleton
 * stacks the same way the loaded card will.
 */
</script>

<template>
  <!-- Real cards are `audio-file-issue-card-<CODE>`, and the e2e specs find them
       with the prefix selector `[data-testid^="audio-file-issue-card-"]`. This
       testid must stay OUTSIDE that prefix: when it shared it, the specs' "wait
       for a card" poll matched the skeletons, clicked one (no click handler),
       and the inspector never opened — the audioFileIssuesSplitView flake. -->
  <Card
    class="@container relative w-full gap-0 border-border/80 p-3 shadow-sm"
    aria-hidden="true"
    data-testid="audio-file-issues-card-skeleton"
  >
    <!-- Pinned code placeholder (top-right, mirrors the live track code). -->
    <Skeleton class="absolute top-3 right-3 h-3 w-16" />

    <div class="flex flex-col gap-3 @xs:flex-row @xs:items-stretch">
      <!-- Cover column: square cover tile + ©year/duration baseline. -->
      <div class="flex w-24 shrink-0 flex-col">
        <Skeleton class="aspect-square w-24" />
        <div class="mt-auto flex items-baseline justify-between pt-1">
          <Skeleton class="h-2.5 w-8" />
          <Skeleton class="h-2.5 w-6" />
        </div>
      </div>

      <!-- Title / artist / album / badges / footer column. -->
      <div class="flex min-w-0 flex-1 flex-col pr-0 @xs:pr-[5.75rem]">
        <Skeleton class="h-4 w-3/4" />
        <Skeleton class="mt-1.5 h-3 w-1/2" />
        <Skeleton class="mt-1.5 h-3 w-2/5" />
        <div class="mt-2 flex flex-wrap items-center gap-2">
          <Skeleton class="h-5 w-16 rounded-full" />
          <Skeleton class="h-5 w-12 rounded-full" />
        </div>
        <div class="mt-auto flex flex-wrap items-baseline gap-x-4 gap-y-1 pt-2">
          <Skeleton class="h-2.5 w-14" />
          <Skeleton class="h-2.5 w-20" />
        </div>
      </div>
    </div>
  </Card>
</template>
